/**
 * 🧹 **A-Frame·MindAR 이 창·문서에 달고 안 떼는 리스너를 우리가 뗀다** (2026-10-13).
 *
 * 카드 화면에 들어갔다 나올 때마다 지난 카메라 장면(`<a-scene>` 과 그 밑 전부)이 남았다 — 운영에서 6번 오가며 잼:
 * 매번 노드 +220 · 리스너 +47 · 힙 +0.5MB, GC 를 여러 번 돌려도 그대로. 힙 스냅샷에서 남은 장면 넷이 모두
 * 「window 리스너 → bind 된 함수 → this.el → <a-scene>」 로 붙들려 있었다. 라이브러리 안쪽이라 고칠 수 없다:
 *  - A-Frame 1.7.1 `<a-scene>`: 이름 없는 `resize`·`load` · 문서의 `fullscreenchange` 4종 — 장면이 떨어져도 안 뗀다
 *  - A-Frame `xr-mode-ui`: `message`·`orientationchange` · `device-orientation-permission-ui`: `deviceorientation`
 *  - A-Frame `<a-scene>`: `navigator.xr` 의 `sessiongranted` — 고친 뒤 다시 재서 나온 두 번째 길(창·문서만 감싸서는 그대로였다)
 *  - MindAR 1.2.5: `window.addEventListener('resize', this._resize.bind(this))` — 같은 함수를 다시 못 만들어 못 뗀다
 *
 * 무대가 살아 있는 동안 **그 라이브러리 코드가 직접** 단 것만 적어 두고(바로 위 호출 줄의 파일로 가른다),
 * 무대가 없어질 때 뗀다. 우리 앱의 리스너는 적지 않는다 — 남의 것을 떼면 더 나쁘다.
 * 🔴 A-Frame 을 처음 불러올 때 한 번 다는 `keydown`·`contextmenu` 는 장면마다가 아니라서 종류 목록에 없다.
 * A-Frame·MindAR 을 올리면 `libGlobals.test.ts` 와 e2e `ar-long-play` 를 다시 볼 것.
 */

/** 장면마다 달리고 안 떼지는 종류(위 목록) */
const PER_SCENE_TYPES = new Set([
  "resize",
  "load",
  "orientationchange",
  "deviceorientation",
  "message",
  "fullscreenchange",
  "mozfullscreenchange",
  "webkitfullscreenchange",
  "MSFullscreenChange",
  "sessiongranted", // navigator.xr — 아래
]);

/** 이 파일에서 온 호출이면 라이브러리 것 — 운영 조각 이름·개발 서버(.vite/deps) 이름 둘 다 */
export const LIB_FILE = /aframe-master|mindar-image-aframe|\.vite\/deps\/aframe/;

/** 스택에서 addEventListener 를 **부른 곳** 한 줄 — V8 은 첫 줄이 "Error", 사파리는 없다 */
export function callerLine(stack: string | undefined): string {
  const lines = (stack ?? "").split("\n").filter((l) => l.trim() && !/^Error\b/.test(l.trim()));
  return lines[1] ?? ""; // [0] 은 이 파일의 감싼 함수 자신
}

type Rec = [EventTarget, string, EventListenerOrEventListenerObject, boolean | AddEventListenerOptions | undefined];

export interface LibGlobalsGuard {
  /** 무대가 없어질 때 — 적어 둔 것을 떼고 감싸기를 푼다 */
  release: () => void;
}

/**
 * @param fakeStack 검사용 — 감싼 함수 안에서 `new Error().stack` 을 만든 것과 **같은 모양**(첫 줄이 감싼 함수)을 준다.
 * 🔴 운영에선 넘기지 않는다: 스택을 다른 함수에서 만들면 그 함수가 한 줄 더 끼어 「부른 곳」이 우리 감싼 함수가 된다
 * (처음 판이 그래서 하나도 못 잡았다 — 운영 빌드 스택을 찍어 보고 알았다).
 */
export function guardLibGlobals(fakeStack?: () => string): LibGlobalsGuard {
  const added: Rec[] = [];
  // [대상, 원래 함수, 원래 자기 속성이었나] — 풀 때 프로토타입 것으로 되돌린다(자기 속성을 남기지 않는다)
  const xr = (navigator as { xr?: EventTarget }).xr; // 크롬 https 에만 있다(사파리엔 없다)
  const targets: [EventTarget, EventTarget["addEventListener"], boolean][] = [window, document, ...(xr ? [xr] : [])].map(
    (t) => [t, t.addEventListener, Object.prototype.hasOwnProperty.call(t, "addEventListener")],
  );
  for (const [t, orig] of targets) {
    t.addEventListener = function (
      this: EventTarget,
      type: string,
      listener: EventListenerOrEventListenerObject | null,
      options?: boolean | AddEventListenerOptions,
    ) {
      if (listener && PER_SCENE_TYPES.has(type)) {
        const stack = fakeStack ? fakeStack() : new Error().stack; // 여기서 만든다 — [0] 이 이 함수, [1] 이 부른 곳
        if (LIB_FILE.test(callerLine(stack))) added.push([t, type, listener, options]);
      }
      return orig.call(this, type, listener, options);
    } as EventTarget["addEventListener"];
  }
  return {
    release: () => {
      for (const [t, orig, own] of targets) {
        if (own) t.addEventListener = orig;
        else delete (t as { addEventListener?: unknown }).addEventListener;
      }
      for (const [t, type, listener, options] of added) t.removeEventListener(type, listener, options);
      added.length = 0;
    },
  };
}
